import React, { useMemo } from 'react';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import {
    RhCheckbox,
    RhDateInput,
    RhFormWrapper,
    RHTextField,
} from '../../../../../dum';
import {
    FIELD_LABELS,
    ORDER_SALE_FIELD_LABELS,
    shortLabel,
    transformToNumber,
} from '../../../../../../helpers';
import {
    clearSalesModule,
    useUpdateOrderSaleDetailsMutation,
} from '../../../../../../services';
import { FormFieldSection } from '../../form-field-section';
import { RequireSupplementInput } from '../order-sale/inputs/require-supplement/require-supplement';
import { SupplementCodeInput } from '../order-sale/inputs/supplement-code/supplement-code';
import { RequireCreditNoteInput } from '../order-sale/inputs/require-credit-node/require-credit-note';
// Note: the credit-note input files are cross-named — credit-note-amount.tsx
// exports the *code* input, credit-note-code.tsx exports the *amount* input.
import { CreditNoteCodeInput } from '../order-sale/inputs/credit-note-amount/credit-note-amount';
import { CreditNoteAmountInput } from '../order-sale/inputs/credit-note-code/credit-note-code';
import { OrderSaleUtilidades } from '../order-sale/order-sale-utilidades';

interface IOrderSaleDetailsChange {
    notes?: string;
    expected_payment_date?: string | null;
    require_supplement: boolean;
    supplement_code?: string | null;
    require_credit_note: boolean;
    credit_note_code?: string | null;
    credit_note_amount?: number | null;
    canceled: boolean;
}

interface IOrderSaleDetailsChangeFormProps {
    orderSaleId: number;
    currentNotes?: string | null;
    currentExpectedPaymentDate?: string | null;
    currentRequireSupplement?: boolean | null;
    currentSupplementCode?: string | null;
    currentRequireCreditNote?: boolean | null;
    currentCreditNoteCode?: string | null;
    currentCreditNoteAmount?: number | null;
    currentCanceled?: boolean | null;
    // Read-only inputs to the nota de crédito tool (never submitted).
    subtotal?: number | null;
    tax?: number | null;
    totalWithTax?: number | null;
    realPaymentsTotal?: number | null;
    onSuccess: () => void;
}

// Lets sales users amend the optional, side-effect-free fields of a sale
// (expected payment date, supplement, credit note, canceled) without
// going through the full sale upsert and its status-1 edit lock. The financial
// fields (tax / automatic tax) are intentionally excluded — they would force a
// totals recompute, which the lightweight updateOrderSaleDetails endpoint avoids.
export function OrderSaleDetailsChangeForm({
    orderSaleId,
    currentNotes,
    currentExpectedPaymentDate,
    currentRequireSupplement,
    currentSupplementCode,
    currentRequireCreditNote,
    currentCreditNoteCode,
    currentCreditNoteAmount,
    currentCanceled,
    subtotal,
    tax,
    totalWithTax,
    realPaymentsTotal,
    onSuccess,
}: IOrderSaleDetailsChangeFormProps) {
    const [mutation, { loading }] = useUpdateOrderSaleDetailsMutation({
        update: (cache) => {
            clearSalesModule(cache);
        },
    });

    const initialValues = useMemo<IOrderSaleDetailsChange>(() => {
        return {
            notes: currentNotes || '',
            expected_payment_date: currentExpectedPaymentDate || null,
            require_supplement: currentRequireSupplement || false,
            supplement_code: currentSupplementCode || '',
            require_credit_note: currentRequireCreditNote || false,
            credit_note_code: currentCreditNoteCode || '',
            credit_note_amount: currentCreditNoteAmount || 0,
            canceled: currentCanceled || false,
        };
    }, [
        currentNotes,
        currentExpectedPaymentDate,
        currentRequireSupplement,
        currentSupplementCode,
        currentRequireCreditNote,
        currentCreditNoteCode,
        currentCreditNoteAmount,
        currentCanceled,
    ]);

    const validationSchema: SchemaOf<IOrderSaleDetailsChange> = yup.object({
        notes: yup.string(),
        expected_payment_date: yup.string().notRequired().nullable(),
        require_supplement: yup.boolean().required(),
        supplement_code: yup.string().notRequired().nullable(),
        require_credit_note: yup.boolean().required(),
        credit_note_code: yup.string().notRequired().nullable(),
        credit_note_amount: yup
            .number()
            .transform(transformToNumber)
            .notRequired()
            .nullable(),
        canceled: yup.boolean().required(),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading}
            onSubmit={async (formData) => {
                try {
                    await mutation({
                        variables: {
                            OrderSaleDetailsInput: {
                                order_sale_id: orderSaleId,
                                notes: formData.notes || '',
                                expected_payment_date:
                                    formData.expected_payment_date || null,
                                require_supplement: formData.require_supplement,
                                supplement_code: formData.supplement_code || '',
                                require_credit_note:
                                    formData.require_credit_note,
                                credit_note_code:
                                    formData.credit_note_code || '',
                                credit_note_amount:
                                    formData.credit_note_amount || 0,
                                canceled: formData.canceled,
                            },
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
        >
            <FormFieldSection title={'Resumen'}>
                <RhDateInput
                    name={'expected_payment_date'}
                    label={shortLabel(
                        ORDER_SALE_FIELD_LABELS.expected_payment_date,
                    )}
                />
            </FormFieldSection>
            <FormFieldSection title={'Complemento de pago'}>
                <RequireSupplementInput />
                <SupplementCodeInput />
            </FormFieldSection>
            <FormFieldSection title={'Nota de crédito'}>
                <RequireCreditNoteInput />
                <CreditNoteCodeInput />
                <CreditNoteAmountInput />
            </FormFieldSection>
            {/* Prices can't change in this dialog, so the saved totals are
                already the live ones. */}
            <FormFieldSection title={'Herramientas'}>
                <OrderSaleUtilidades
                    subtotal={subtotal || 0}
                    tax={tax || 0}
                    totalWithTax={totalWithTax || 0}
                    realPaymentsTotal={realPaymentsTotal || 0}
                />
            </FormFieldSection>
            <FormFieldSection title={'Notas'}>
                <RHTextField
                    name={'notes'}
                    label={shortLabel(FIELD_LABELS.notes)}
                    rows={3}
                />
            </FormFieldSection>
            <FormFieldSection title={'Control de la venta'}>
                <RhCheckbox
                    name={'canceled'}
                    label={shortLabel(FIELD_LABELS.canceled)}
                />
            </FormFieldSection>
        </RhFormWrapper>
    );
}
